/**
 * 断言告警流（端侧确定性核验引擎的输出）。
 *
 * 支持按阵营 / 环节过滤；告警按时间倒序展示，标红并同时给出
 * 命中原文片段（snippet）与纠错口径（message）。
 */
import { useState } from 'react';
import { SIDES, sideLabel, type Side } from '@zongheng/core-schema';
import { useArena } from '../../state/ArenaContext.tsx';
import { Badge, EmptyState, cx } from '../ui/primitives.tsx';
import { SEVERITY_LABEL, formatTimeOfDay } from '../../export/report.ts';

type SideFilter = 'ALL' | Side;
type StageFilter = 'ALL' | number;

export function AlertFeed() {
  const { state } = useArena();
  const [sideFilter, setSideFilter] = useState<SideFilter>('ALL');
  const [stageFilter, setStageFilter] = useState<StageFilter>('ALL');

  const stageOptions = [...new Set(state.alerts.map((alert) => alert.stageIndex))].sort(
    (a, b) => a - b,
  );
  const alerts = state.alerts
    .filter((alert) => sideFilter === 'ALL' || alert.side === sideFilter)
    .filter((alert) => stageFilter === 'ALL' || alert.stageIndex === stageFilter)
    .sort((a, b) => b.at - a.at);

  const severityTone = (severity: string): 'violation' | 'warning' | 'neutral' =>
    severity === 'VIOLATION' ? 'violation' : severity === 'WARN' ? 'warning' : 'neutral';

  return (
    <section className="rounded-xl border border-white/10 bg-bg-surface/80 backdrop-blur">
      <header className="flex flex-wrap items-start justify-between gap-2 border-b border-white/5 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold tracking-wide text-slate-100">断言告警流</h2>
          <p className="mt-0.5 text-xs text-slate-400">
            口径核验 · 共 {state.alerts.length} 条 · 违规 {state.alerts.filter((a) => a.severity === 'VIOLATION').length} 条
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
          <select
            data-testid="alert-filter-side"
            value={sideFilter}
            onChange={(event) => setSideFilter(event.target.value as SideFilter)}
            className="rounded-md border border-white/10 bg-black/30 px-1.5 py-1 text-[11px] text-slate-200"
          >
            <option value="ALL">全部阵营</option>
            {SIDES.map((side) => (
              <option key={side} value={side}>
                {sideLabel(side)}
              </option>
            ))}
          </select>
          <select
            data-testid="alert-filter-stage"
            value={stageFilter}
            onChange={(event) =>
              setStageFilter(event.target.value === 'ALL' ? 'ALL' : Number(event.target.value))
            }
            className="rounded-md border border-white/10 bg-black/30 px-1.5 py-1 text-[11px] text-slate-200"
          >
            <option value="ALL">全部环节</option>
            {stageOptions.map((stageIndex) => (
              <option key={stageIndex} value={stageIndex}>
                环节 {stageIndex}
              </option>
            ))}
          </select>
        </div>
      </header>
      <div className="p-3">
        {state.alerts.length === 0 ? (
          <EmptyState
            title="未发现口径错误"
            detail="辩手端（或模拟转录）提交发言后，命中 forbiddenMisquotes 的片段会在这里实时标红。"
          />
        ) : alerts.length === 0 ? (
          <EmptyState title="当前过滤条件下没有告警" detail="可切换阵营 / 环节筛选。" />
        ) : (
          <ul className="space-y-1.5" data-testid="coach-alert-list">
            {alerts.map((alert) => (
              <li
                key={alert.id}
                data-testid="alert-item"
                data-severity={alert.severity}
                className={cx(
                  'rounded-lg border px-2.5 py-2',
                  alert.severity === 'VIOLATION'
                    ? 'border-assert-violation/40 bg-assert-violation/[0.08]'
                    : alert.severity === 'WARN'
                      ? 'border-clock-warning/40 bg-clock-warning/[0.08]'
                      : 'border-white/10 bg-black/20',
                )}
              >
                <div className="flex flex-wrap items-center justify-between gap-1">
                  <div className="flex items-center gap-1.5">
                    <Badge tone={severityTone(alert.severity)}>
                      {SEVERITY_LABEL[alert.severity] ?? alert.severity}
                    </Badge>
                    <span className={alert.side === 'AFF' ? 'text-xs text-team-aff' : 'text-xs text-team-neg'}>
                      {sideLabel(alert.side)}
                    </span>
                    <span className="text-[11px] text-slate-500">环节 {alert.stageIndex}</span>
                  </div>
                  <span className="mono text-[10px] text-slate-600">{formatTimeOfDay(alert.at)}</span>
                </div>
                <p className="mt-1 text-xs font-medium text-assert-violation">{alert.message}</p>
                <p className="mt-0.5 rounded bg-black/30 px-1.5 py-1 text-[11px] text-slate-400">
                  命中原文：{alert.snippet}
                  {alert.matchedText && (
                    <span className="ml-1 rounded bg-assert-violation/20 px-1 text-assert-violation">
                      {alert.matchedText}
                    </span>
                  )}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
